import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Input, Select, Modal, Form,
  message, Popconfirm, Row, Col, Statistic, Badge,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  EyeOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import {
  getNewsList, createNews, updateNews, deleteNews, toggleNewsPublish,
} from '../services/adminApi';

const { TextArea } = Input;
const { Search } = Input;

// 分类配置
const CATEGORY_MAP: Record<string, { color: string; label: string }> = {
  policy: { color: 'blue', label: '电价政策' },
  industry: { color: 'green', label: '行业动态' },
  promotion: { color: 'orange', label: '优惠活动' },
  guide: { color: 'purple', label: '使用指南' },
  notice: { color: 'red', label: '公告通知' },
};

const CATEGORY_OPTIONS = [
  { value: 'policy', label: '电价政策' },
  { value: 'industry', label: '行业动态' },
  { value: 'promotion', label: '优惠活动' },
  { value: 'guide', label: '使用指南' },
  { value: 'notice', label: '公告通知' },
];

const NewsManagement: React.FC = () => {
  const [newsList, setNewsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState<{ category?: string; keyword?: string; isPublished?: boolean }>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNews, setEditingNews] = useState<any>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewNews, setPreviewNews] = useState<any>(null);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [form] = Form.useForm();

  const fetchNewsList = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params: any = { page, pageSize, ...filters };
      const res = await getNewsList(params);
      const data = res.data?.data;
      if (data) {
        setNewsList(data.list || []);
        setPagination({ current: data.page, pageSize: data.pageSize, total: data.total });
      }
    } catch (e) {
      message.error('获取新闻列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [allRes, pubRes, draftRes] = await Promise.all([
        getNewsList({ page: 1, pageSize: 1 }),
        getNewsList({ page: 1, pageSize: 1, isPublished: true }),
        getNewsList({ page: 1, pageSize: 1, isPublished: false }),
      ]);
      setStats({
        total: allRes.data?.data?.total || 0,
        published: pubRes.data?.data?.total || 0,
        draft: draftRes.data?.data?.total || 0,
      });
    } catch (_) {}
  };

  useEffect(() => { fetchNewsList(); fetchStats(); }, []);
  useEffect(() => { fetchNewsList(); }, [filters]);

  const handleCreate = () => {
    setEditingNews(null);
    form.resetFields();
    form.setFieldsValue({ category: 'industry', isPublished: false });
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingNews(record);
    form.setFieldsValue({
      title: record.title,
      summary: record.summary,
      content: record.content,
      category: record.category,
      image: record.image,
      author: record.author,
      tags: record.tags ? JSON.parse(record.tags) : [],
      isPublished: record.isPublished,
    });
    setModalVisible(true);
  };

  const handlePreview = (record: any) => {
    setPreviewNews(record);
    setPreviewVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingNews) {
        await updateNews(editingNews.id, values);
        message.success('新闻更新成功');
      } else {
        await createNews(values);
        message.success('新闻创建成功');
      }
      setModalVisible(false);
      fetchNewsList(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (e: any) {
      if (e?.errorFields) return; // 表单验证失败
      message.error(e?.message || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteNews(id);
      message.success('删除成功');
      fetchNewsList(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (_) {
      message.error('删除失败');
    }
  };

  const handleTogglePublish = async (id: number) => {
    try {
      const res = await toggleNewsPublish(id);
      const statusText = res.data?.data?.isPublished ? '已发布' : '已下架';
      message.success(`新闻${statusText}`);
      fetchNewsList(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (_) {
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      ellipsis: true,
      render: (text: string, record: any) => (
        <a onClick={() => handlePreview(record)}>{text}</a>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (v: string) => {
        const cfg = CATEGORY_MAP[v] || { color: 'default', label: v };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 100,
    },
    {
      title: '阅读量',
      dataIndex: 'readCount',
      key: 'readCount',
      width: 80,
      sorter: (a: any, b: any) => a.readCount - b.readCount,
    },
    {
      title: '状态',
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 90,
      render: (v: boolean) => (
        v ? <Badge status="success" text="已发布" /> : <Badge status="default" text="草稿" />
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
            预览
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button
            type="link" size="small"
            icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleTogglePublish(record.id)}
          >
            {record.isPublished ? '下架' : '发布'}
          </Button>
          <Popconfirm title="确定删除这篇新闻？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="全部新闻" value={stats.total} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="已发布" value={stats.published} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="草稿" value={stats.draft} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#999' }} />
          </Card>
        </Col>
      </Row>

      {/* 新闻列表 */}
      <Card
        title="新闻管理"
        extra={
          <Space>
            <Search
              placeholder="搜索标题/摘要"
              allowClear
              style={{ width: 200 }}
              onSearch={(v) => setFilters((f) => ({ ...f, keyword: v || undefined }))}
            />
            <Select
              placeholder="分类筛选"
              allowClear
              style={{ width: 120 }}
              onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
              options={CATEGORY_OPTIONS}
            />
            <Select
              placeholder="发布状态"
              allowClear
              style={{ width: 120 }}
              onChange={(v) => setFilters((f) => ({ ...f, isPublished: v }))}
              options={[
                { value: true, label: '已发布' },
                { value: false, label: '草稿' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { fetchNewsList(); fetchStats(); }}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>发布新闻</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={newsList}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => fetchNewsList(page, pageSize),
          }}
          size="middle"
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建/编辑弹窗 */}
      <Modal
        title={editingNews ? '编辑新闻' : '发布新闻'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }, { max: 200, message: '标题不能超过200字' }]}>
            <Input placeholder="请输入新闻标题" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
                <Select options={CATEGORY_OPTIONS} placeholder="请选择分类" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="author" label="作者" rules={[{ required: true, message: '请输入作者' }]}>
                <Input placeholder="请输入作者名称" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="summary" label="摘要" rules={[{ required: true, message: '请输入摘要' }]}>
            <TextArea rows={2} placeholder="请输入新闻摘要" />
          </Form.Item>
          <Form.Item name="content" label="正文" rules={[{ required: true, message: '请输入正文内容' }]}>
            <TextArea rows={8} placeholder="请输入新闻正文内容" />
          </Form.Item>
          <Form.Item name="image" label="封面图片URL">
            <Input placeholder="请输入封面图片链接（可选）" />
          </Form.Item>
          <Form.Item name="isPublished" label="立即发布" valuePropName="checked" initialValue={false}>
            <Select
              options={[
                { value: true, label: '是 - 立即发布' },
                { value: false, label: '否 - 保存为草稿' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览弹窗 */}
      <Modal
        title={previewNews?.title}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={640}
      >
        {previewNews && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color={CATEGORY_MAP[previewNews.category]?.color}>
                {CATEGORY_MAP[previewNews.category]?.label || previewNews.category}
              </Tag>
              <span style={{ color: '#999', marginLeft: 8 }}>
                {previewNews.author} · {previewNews.publishedAt ? new Date(previewNews.publishedAt).toLocaleString('zh-CN') : '未发布'}
              </span>
              <span style={{ color: '#999', marginLeft: 8 }}>阅读 {previewNews.readCount}</span>
            </div>
            {previewNews.image && (
              <img src={previewNews.image} alt="封面" style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />
            )}
            <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 16 }}>{previewNews.summary}</p>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{previewNews.content}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NewsManagement;
